import { Modal } from "antd";
import { ContaForm, ContaAnexo } from "features/conta/typings";
import {
  OperacaoEnum,
  TipoEntidadeEnum,
  TipoRecorrenciaEnum,
  FormaRecorrenciaEnum,
} from "core/resources/enums/conta";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import FormConta from ".";
import FooterModal from "components/Button/FooterModal";
import { useFindConta } from "features/conta/hooks";
import moment from "moment";
import { showErrorAxios, showSuccess } from "infra/helpers/alerta";
import { useMemo } from "react";
import { Tag, Box } from "@chakra-ui/react";
import useSaveConta from "features/conta/hooks/useSaveConta";
import SkeletonForm from "./SkeletonForm";
import { useInvalidateQuery } from "infra/packages/react-query";
import { TipoDivisaoContaEnum } from "resources/enums/conta";
import { roundTo } from "round-to";
import { AxiosError } from "axios";
import OutrasParcelas from "./components/OutrasParcelas";
import { TipoAnexoContaEnum } from "core/features/conta/typings";
interface ModalFormContaProps {
  operacao: OperacaoEnum;
  isOpen: boolean;
  onClose: () => any;
  onSuccess: (result: any) => any;
  id?: string;
  showRegisterAnother?: boolean;
  defaultData?: Partial<ContaForm>;
  onSuccessImmediatelyAfterSave?: boolean;
  requiredLiquidar?: boolean;
  fixedDataMovimentacao?: string;
}

const defaultValues: Partial<ContaForm> = {
  repetir: false,
  retencaoImposto: false,
  anexarArquivos: false,
  tipoEntidadeId: TipoEntidadeEnum.Fornecedor,
  dataCompetencia: moment().format("YYYY-MM"),
  dataVencimento: moment().format("YYYY-MM-DD"),
  formasRecebimentoIds: [],
  composicoes: [
    {
      categoriaFinanceiraId: undefined,
      valor: undefined,
    },
  ],
  retencoesImpostos: [],
  tiposAnexosIds: [],
  imposto: {
    inss: 0,
    irrf: 0,
    issqn: 0,
    pisConfisCsll: 0,
  },
  descricao: "",
  liquidarConta: false,
};

function ModalFormConta({
  operacao,
  isOpen,
  onClose,
  onSuccess,
  id,
  showRegisterAnother = true,
  defaultData,
  onSuccessImmediatelyAfterSave = true,
  requiredLiquidar,
  fixedDataMovimentacao,
}: ModalFormContaProps) {
  const { doInvalidateQueryWithStringKey } = useInvalidateQuery();
  const { data, isFetching, refetch } = useFindConta(id);
  const [arquivosExcluidos, setArquivosExcluidos] = useState<string[]>([]);
  const [fixedGerarCobranca, setFixedGerarCobranca] = useState(false);
  const [editarOutraParcelaId, setEditarOutraParcelaId] = useState<string>();

  const [registerAnother, setRegisterAnother] = useState(false);

  const { mutate: saveContaPagar, isLoading: isLoadingSave } =
    useSaveConta(operacao);

  const { control, watch, reset, setValue, getValues, handleSubmit } =
    useForm<ContaForm>({
      defaultValues: {
        dataCompetencia: null,
        pagarConta: {
          dataMovimentacao: fixedDataMovimentacao,
        },
        tiposAnexosIds:
          operacao === OperacaoEnum.Despesa
            ? [
                TipoAnexoContaEnum.Comprovante,
                TipoAnexoContaEnum.DocumentoFiscal,
                TipoAnexoContaEnum.DocumentoPagamento,
                TipoAnexoContaEnum.Outros,
                TipoAnexoContaEnum.DocumentoAcessorio,
              ]
            : [],
        calcularJurosAutomatico:
          operacao === OperacaoEnum.Receita ? true : undefined,
        ...defaultValues,
        ...defaultData,
      },
      mode: "all",
    });

  const form = watch();

  useEffect(() => {
    if (data) {
      setFixedGerarCobranca(!!data.gerarCobranca);
      reset({
        ...defaultData,
        pagarConta: {
          dataMovimentacao: fixedDataMovimentacao,
        },
        ...data,
        formasRecebimentoIds: data.gerarCobranca
          ? data.formasRecebimento.map((forma) => forma.id)
          : [],
        parcelasExistentes: data.parcelas,
        tiposAnexosIds: data.tiposAnexosIds ?? [],
        anexarArquivos: !!data.anexos?.length,
        files: !!data.anexos?.length
          ? data?.anexos?.map((anexo) => ({
              id: anexo.id,
              nome: anexo.nome,
              referencia: anexo.referencia,
              mimetype: anexo.mimetype,
              tipoAnexoId: anexo.tipoAnexoId,
            }))
          : [],

        parcelas: [],
        repetir: false,
        numeroParcelas: undefined,
      });
    } else if (defaultData) {
      reset({
        ...defaultValues,
        ...defaultData,
      });
    }
  }, [data, defaultData, fixedDataMovimentacao, reset]);

  useEffect(() => {
    if (!isOpen) {
      reset({ ...defaultValues, ...defaultData });
    }
  }, [data, defaultData, isOpen, reset]);

  const onError = (err: AxiosError<string>, variables: ContaForm) => {
    const action =
      err?.response?.status === 409
        ? () => onSave({ ...variables, pularChecagemDespesa: true })
        : undefined;

    showErrorAxios(err, undefined, action);
  };

  const onSave = async (data: ContaForm) => {
    const _data: ContaForm = {
      ...data,
      liquidarConta: requiredLiquidar || data.liquidarConta,
      id,
      formaRecorrenciaId:
        data.tipoRecorrenciaId === TipoRecorrenciaEnum.Fixa
          ? FormaRecorrenciaEnum.Mensal
          : undefined,
      anexos: (data.files?.filter((file) => !(file instanceof File)) ??
        []) as ContaAnexo[],
    };

    delete _data.files;

    saveContaPagar(_data, {
      onSuccess: (result) => {
        if (onSuccessImmediatelyAfterSave) {
          setTimeout(() => {
            onSuccess(result);
          }, 200);
        }

        reset({ ...defaultValues });

        const invalidateDetalhe = () => {
          if (id) {
            doInvalidateQueryWithStringKey(`/v1/conta/${id}/completa`);
          }
        };

        if (!registerAnother) {
          showSuccess().then(({ isConfirmed }) => {
            if (isConfirmed) {
              onSuccess(result);
            }
            invalidateDetalhe();
          });
        } else {
          showSuccess().then(() => invalidateDetalhe());
        }
      },
      onError,
    });
  };

  const onSubmit = async (data: ContaForm) => {
    try {
      await onSave?.({
        ...data,
        arquivosExcluidos,
        operacao,
      });
    } catch (err) {
      showErrorAxios(err);
    }
  };

  const title = useMemo(() => {
    let result = null;

    if (operacao === OperacaoEnum.Despesa) {
      if (id) {
        result = "Editar Despesa";
      } else {
        result = "Nova Despesa";
      }
    } else if (id) {
      result = "Editar Receita";
    } else {
      result = "Nova Receita";
    }

    if (data?.dataPagamento) {
      result = (
        <Box>
          {result}{" "}
          <Tag ml="16px" variant="solid" colorScheme="green">
            Já Liquidada
          </Tag>
        </Box>
      );
    }

    return result;
  }, [data?.dataPagamento, id, operacao]);

  const isDisabled = () => {
    if (
      form.tipoDivisaoContaId ===
        TipoDivisaoContaEnum.DividirValorLiquidoEntreParcelas &&
      !form.grupoConta
    ) {
      const total = form.composicoes?.reduce(
        (acc, next) =>
          (acc += (next?.valor ?? 0) * (next?.operacao ?? 0) * operacao),
        0
      );

      const valorParcelas =
        form.parcelas?.reduce((acc, next) => (acc += next.valor), 0) || 0;

      if (roundTo(valorParcelas, 2) !== roundTo(total, 2)) {
        return true;
      }
    }

    return false;
  };

  return (
    <Modal
      centered
      visible={isOpen}
      footer={
        <FooterModal
          isDisabled={isFetching || isDisabled()}
          onSubmit={() => {
            handleSubmit(onSubmit)();
          }}
          showRegisterAnother={showRegisterAnother}
          registerAnother={registerAnother}
          setRegisterAnother={setRegisterAnother}
          isLoadingSave={isLoadingSave!}
          onClose={onClose}
        />
      }
      title={title}
      destroyOnClose
      onCancel={onClose}
      maskClosable={false}
      closable={true}
      width={"min(1300px, 90vw)"}
      bodyStyle={{
        maxHeight: "80vh",
        overflow: "auto",
        overflowX: "hidden",
        paddingBottom: 16,
      }}
    >
      <>
        {!isFetching ? (
          <>
            <FormConta
              control={control}
              setValue={setValue}
              form={form}
              requiredLiquidar={requiredLiquidar}
              fixedDataMovimentacao={fixedDataMovimentacao}
              fixedGerarCobranca={fixedGerarCobranca}
              getValues={getValues}
              operacao={operacao}
              isUpdate={!!id}
              onChangeExcludes={(id) =>
                setArquivosExcluidos((state) => [...state, id])
              }
            />
            {form.grupoConta && form.parcelasExistentes?.length ? (
              <Box position="absolute" top={2} right={16}>
                <OutrasParcelas
                  outrasParcelas={form.parcelasExistentes ?? []}
                  onClickParcela={(id) => setEditarOutraParcelaId(id)}
                  operacao={operacao}
                />
              </Box>
            ) : null}

            {editarOutraParcelaId ? (
              <ModalFormConta
                isOpen={!!editarOutraParcelaId}
                onClose={() => setEditarOutraParcelaId(undefined)}
                onSuccess={() => {
                  setEditarOutraParcelaId(undefined);
                  refetch();
                }}
                operacao={operacao}
                id={editarOutraParcelaId}
                showRegisterAnother={false}
              />
            ) : null}
          </>
        ) : (
          <SkeletonForm />
        )}
      </>
    </Modal>
  );
}

export default ModalFormConta;
